import type { Card } from './types';
import { findSets, hasValidSet } from './setEngine.js';
import type { PeerRole } from '../network/types';

export interface ClashScores {
  host: number;
  guest: number;
}

export const CLASH_WINNING_SCORE = 14;
export const CLASH_SELECTION_TIMEOUT_MS = 5000;

/**
 * Resolves who acquired the lock when both players tap at near-simultaneous times.
 * Earliest timestamp wins; in case of an exact tie, Host is the arbiter.
 */
export function arbitrateLockConflict(
  hostTimestamp: number,
  guestTimestampAdjusted: number
): PeerRole {
  if (hostTimestamp < guestTimestampAdjusted) {
    return 'host';
  }
  if (guestTimestampAdjusted < hostTimestamp) {
    return 'guest';
  }
  return 'host'; // Host tie-breaker
}

/**
 * Calculates symmetric perspective scores for the local UI.
 * From each player's POV: they are Blue (Self), opponent is Red.
 */
export function getSymmetricScores(
  scores: ClashScores,
  localRole: PeerRole
): { blueScore: number; redScore: number } {
  if (localRole === 'host') {
    return { blueScore: scores.host, redScore: scores.guest };
  } else {
    return { blueScore: scores.guest, redScore: scores.host };
  }
}

/**
 * Updates scores after a valid set or penalty.
 * Scores cannot fall below 0.
 */
export function applyScoreDelta(
  scores: ClashScores,
  role: PeerRole,
  delta: number
): ClashScores {
  return {
    host: role === 'host' ? Math.max(0, scores.host + delta) : scores.host,
    guest: role === 'guest' ? Math.max(0, scores.guest + delta) : scores.guest,
  };
}

/**
 * Checks if a player has reached the winning threshold or if the deck/board is cleared.
 */
export function checkClashGameOver(
  scores: ClashScores,
  board: Card[],
  deck: Card[]
): { isOver: boolean; winnerRole: PeerRole | 'tie' | null } {
  if (scores.host >= CLASH_WINNING_SCORE) {
    return { isOver: true, winnerRole: 'host' };
  }
  if (scores.guest >= CLASH_WINNING_SCORE) {
    return { isOver: true, winnerRole: 'guest' };
  }

  // Deck exhausted and no sets on board
  if (deck.length === 0 && !hasValidSet(board)) {
    if (scores.host > scores.guest) return { isOver: true, winnerRole: 'host' };
    if (scores.guest > scores.host) return { isOver: true, winnerRole: 'guest' };
    return { isOver: true, winnerRole: 'tie' };
  }

  return { isOver: false, winnerRole: null };
}
